/**
 * annotationFieldTypes.js - Pinia store for per-type annotation sizing rules.
 *
 * The server owns the rules (defaults expressed as multipliers of the
 * document's median body-text line height, plus per-type absolute minimums).
 * The UI fetches them once and uses them for:
 *   - Drop-time default sizing in FieldEditor (matches what the server
 *     resolver would produce when an API/MCP caller omits dimensions).
 *   - Resize handle minimums in PdfPageViewer (matches what the server
 *     would clamp to on save - no more silent clamp-on-save divergence).
 *
 * Lazy-loaded: ensure() is called by FieldEditor on mount so the rules are
 * ready before the user can drop a field. Cached for the app lifetime -
 * the data is product config, not tenant data, and changes only when the
 * platform tunes a field type.
 */
import { defineStore } from 'pinia';
import { annotationFieldTypes as api } from '../services/signApi';

export const useAnnotationFieldTypesStore = defineStore('annotationFieldTypes', {
    state: () => ({
        // Map of typeName → { widthMultiplier, heightMultiplier, minWidthPt, minHeightPt }
        types: null,
        // Page-height fraction used as synthetic line height when a page has
        // no extractable text layer (scanned/image-only PDFs).
        fallbackLineHeightFraction: 0.018,
        loading: false,
        error: null,
        loaded: false,
    }),
    actions: {
        /**
         * Loads the rules table if not already loaded. Safe to call multiple
         * times - concurrent calls share the same in-flight promise so the
         * server only sees one request even if several components mount at
         * once. Resolves once the store has data.
         */
        async ensure() {
            if (this.loaded) return;
            if (this._inflight) return this._inflight;

            this.loading = true;
            this.error = null;
            this._inflight = (async () => {
                try {
                    const data = await api.get();
                    this.types = data.types || {};
                    this.fallbackLineHeightFraction =
                        typeof data.fallbackLineHeightFraction === 'number'
                            ? data.fallbackLineHeightFraction
                            : 0.018;
                    this.loaded = true;
                } catch (err) {
                    // Fetch failure leaves loaded=false; callers will use
                    // their hardcoded fallbacks. Logging only - don't throw,
                    // since field editing should still work in degraded mode.
                    this.error = err?.message || 'Failed to load annotation sizing rules';
                    console.warn('[annotationFieldTypes] failed to load rules:', err);
                } finally {
                    this.loading = false;
                    this._inflight = null;
                }
            })();
            return this._inflight;
        },

        /**
         * Returns the rule for a given annotation type, or null if not
         * registered (or if the rules failed to load). Callers should treat
         * null as "use hardcoded fallback dimensions" - see resolveDefaults.
         */
        ruleFor(type) {
            return this.types?.[type] || null;
        },
    },
});
